import classNames from "classnames";
import { Button } from "components/DesignSystem/Button/Button";
import ToolTip from "components/design/toolTip";
import { Cross } from "components/icons/Cross";
import { CLICKED_LEGEND_FILTER_ON_FILINGS_TIMELINE } from "constants/analyticsEvents";
import {
  TIMELINE_LEGEND_FILTER,
  TIMELINE_LEGEND_FILTER_TOOLTIP,
  TIMELINE_LEGEND_LABEL,
  TIMELINE_STATUS,
  type TimelineLegendFilter,
  type TimelineStatus,
} from "constants/filingsTimeline";
import { useAnalytics } from "hooks/useAnalytics";
import { useTimelineContext } from "./TimelineContext";
import { TimelineStatusDot } from "./TimelineStatusDot";

type TimelineLegendItemProps = {
  filter: TimelineLegendFilter;
  count: number;
};

const LABEL_BUTTON_CLASS =
  "!t-h-auto !t-min-h-0 !t-p-0 !t-font-normal hover:!t-no-underline focus:!t-ring-0 focus-visible:!t-ring-2 focus-visible:!t-ring-purple-20";

const getDotStatus = (filter: TimelineLegendFilter): TimelineStatus => {
  if (filter === TIMELINE_LEGEND_FILTER.OVERDUE) {
    return TIMELINE_STATUS.PENDING_ON_YOU;
  }

  return filter;
};

export const TimelineLegendItem = ({
  filter,
  count,
}: TimelineLegendItemProps) => {
  const { activeFilter, onApplyFilter, onClearFilter, onPreviewFilter } =
    useTimelineContext();
  const { trackEvent } = useAnalytics();

  const isActive = activeFilter === filter;
  const label = `${TIMELINE_LEGEND_LABEL[filter]} (${count})`;

  const handleApply = () => {
    if (isActive) return;

    trackEvent(CLICKED_LEGEND_FILTER_ON_FILINGS_TIMELINE, { status: filter });
    onApplyFilter(filter);
  };

  const handlePreview = () => onPreviewFilter(filter);
  const handleEndPreview = () => onPreviewFilter(null);

  const labelButton = (
    <Button
      customType="link_neutral"
      size="xxsmall"
      onClick={handleApply}
      aria-pressed={isActive}
      className={LABEL_BUTTON_CLASS}
    >
      <span className="t-flex t-items-center t-gap-1">
        <TimelineStatusDot
          status={getDotStatus(filter)}
          isOverdue={filter === TIMELINE_LEGEND_FILTER.OVERDUE}
        />
        <span
          className={classNames(
            "t-text-caption !t-text-[11px] t-transition-colors t-duration-150 motion-reduce:t-transition-none",
            {
              "t-text-text-100": isActive,
              "t-text-neutral-40 group-hover/legend:t-text-text-100": !isActive,
            }
          )}
        >
          {label}
        </span>
      </span>
    </Button>
  );

  return (
    <span
      onMouseEnter={handlePreview}
      onMouseLeave={handleEndPreview}
      onFocus={handlePreview}
      onBlur={handleEndPreview}
      className={classNames(
        "t-group/legend t-flex t-h-[21px] t-items-center t-gap-1 t-rounded-full t-border t-border-solid t-pl-2 t-pr-1.5 t-transition-colors t-duration-150 motion-reduce:t-transition-none",
        {
          "-t-ml-[19px] -t-mr-[13px] t-border-purple-40 t-bg-purple-0":
            isActive,
          "-t-ml-[9px] -t-mr-[7px] t-border-surface-transparent hover:t-bg-neutral-0":
            !isActive,
        }
      )}
    >
      {isActive ? (
        labelButton
      ) : (
        <ToolTip
          text={TIMELINE_LEGEND_FILTER_TOOLTIP}
          side="top"
          propClass="t-max-w-64 t-cursor-pointer"
          onClick={handleApply}
        >
          {labelButton}
        </ToolTip>
      )}

      {isActive && (
        <Button
          customType="link_neutral"
          size="xxsmall"
          onClick={onClearFilter}
          aria-label={`Clear ${TIMELINE_LEGEND_LABEL[filter]} filter`}
          className="!t-h-3 !t-w-3 !t-min-h-0 !t-p-0"
        >
          <span className="t-flex">
            <Cross size="9" />
          </span>
        </Button>
      )}
    </span>
  );
};
